import { UniButton } from '@/design-system/components/buttons';
import { H6, TextCaption, TextSmall } from '@/design-system/components/typography';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { useNavigation } from '@react-navigation/native';
import { LayoutContext } from 'contexts/LayoutContext';
import { styled, SxProp, View } from 'dripsy';
import _ from 'lodash';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Image } from 'react-native';
import { RootStackRoutes } from 'types';
import { GetBrokersResponseItem } from '@utx/utx-client';
import SVGBinance from '../../../../assets/svg/BinanceBrokerBanner.svg';
import SVGBannerBitfinex from '../../../../assets/svg/BitfinexBrokerBanner.svg';
import { ExchangeSubAccountType } from '../../enum';
import { ExchangeSubAccountStackRoutes } from '../../exchange.navigator';
import { UseGetCalcFeeProp } from '../../hooks/useGetCalcFee.hook';
import { ScaledImage } from '../components/ScaledImage';

export interface SelectExchangeProps {
  data: GetBrokersResponseItem;
  calcFee: UseGetCalcFeeProp;
  onResult: () => void;
}

export const SelectExchangeItem: React.FC<SelectExchangeProps> = (props) => {
  const navigation = useNavigation();
  const { data, calcFee, onResult } = props;
  const {
    monthlyData: { fee: monthlyFee },
    applicationData: { fee: applicationFee }
  } = calcFee;
  const { userFee: applicationUserFee, feeCurrency: applicationFeeCurrency } = applicationFee || {};
  const { userFee: monthlyUserFee, feeCurrency: monthlyFeeCurrency } = monthlyFee || {};
  const metadata = data.metadata || {};
  const { feeSummary } = metadata || {};
  const { estimatedAccountOpeningTime } = feeSummary || {};
  const ImageView = styled(Image)();
  const getIcon = (name: string) => {
    if (name === ExchangeSubAccountType.BITFINEX) {
      return <SVGBannerBitfinex width="100%" />;
    }
    if (name === ExchangeSubAccountType.BINANCE) {
      return <SVGBinance width="100%" />;
    }
    return <ImageView sx={{ width: '100%', height: '100%' }} source={require('../../../../assets/images/ftxBrokerBanner.jpg')} />;
  };

  const getBackgroundColor = (name: string) => {
    if (name === ExchangeSubAccountType.BITFINEX) {
      return '#275D6C';
    }
    if (name === ExchangeSubAccountType.BINANCE) {
      return '#FDFADC';
    }
    return '#3276A7';
  };

  const displayAmountWithCurrency = (amountString: string, currency: string) => {
    const amount = parseFloat(amountString ?? '0');
    return `${amount < 0 ? '-' : ''}${formatAmountByDecimals({ value: Math.abs(amount), decimals: 2 })} ${currency ?? 'HKD'}`;
  };

  const onSelectExchange = () => {
    navigation.navigate(RootStackRoutes.ExchangeSubAccount, {
      screen: ExchangeSubAccountStackRoutes.ConfirmExchange,
      params: {
        data,
        shouldBeFetchFee: _.isEmpty(monthlyFee) || _.isEmpty(applicationFee),
        onResult
      }
    });
  };
  const layout = useContext(LayoutContext);
  const { t } = useTranslation('exchange-sub-account');

  return (
    <View sx={sxStyles.container}>
      <View sx={sxStyles.item}>
        <View sx={{ ...sxStyles.itemBanner, ...{ backgroundColor: getBackgroundColor(data.name) } }}>
          <View sx={sxStyles.iconWrapper}>
            <ScaledImage uri={data.logoUrl ?? ''} />
          </View>
          {getIcon(data.name)}
        </View>
        <View sx={sxStyles.itemView}>
          <View sx={sxStyles.itemContainer}>
            <View sx={sxStyles.applicationFeeView}>
              <TextCaption sx={sxStyles.labelText}>{t('exchange-account::glossary.account-application-fee')}</TextCaption>
              <H6 bold sx={sxStyles.amountText}>
                {displayAmountWithCurrency(applicationUserFee, applicationFeeCurrency)}
              </H6>
            </View>
            <View sx={sxStyles.monthlyFeeView}>
              <TextCaption sx={sxStyles.labelText}>{t('exchange-account::glossary.monthly-fee')}</TextCaption>
              <H6 bold sx={sxStyles.amountText}>
                {displayAmountWithCurrency(monthlyUserFee, !_.isEmpty(monthlyFeeCurrency) ? monthlyFeeCurrency : applicationFeeCurrency)}
              </H6>
            </View>
          </View>
          <View sx={sxStyles.openingTimeText}>
            <TextSmall semiBold>
              {' '}
              {t('exchange-account::glossary.est-account-opening-time-days', { days: estimatedAccountOpeningTime ?? '-' })}{' '}
            </TextSmall>
          </View>
          <UniButton
            label={t('general::select')}
            onPress={() => {
              onSelectExchange();
            }}></UniButton>
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    width: 390,
    height: 365
  },
  item: {
    flex: 1,
    margin: '$5',
    borderRadius: '$radius2',
    overflow: 'hidden',
    backgroundColor: '$backgroundPrimary'
  },
  itemBanner: {
    height: '50%'
  },
  itemView: {
    height: '50%',
    padding: '$5'
  },
  itemContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  applicationFeeView: {
    alignContent: 'left',
    textAlign: 'left'
  },
  monthlyFeeView: {
    alignContent: 'right',
    textAlign: 'right'
  },
  labelText: {
    paddingBottom: '$1',
    color: '$textSecondary'
  },
  openingTimeText: {
    paddingBottom: '$5',
    paddingTop: '$3',
    marginLeft: -4
  },
  iconWrapper: {
    position: 'absolute',
    top: '$0',
    left: '$0',
    opacity: 0
  }
};
